/** Nonmodal, section-scoped product disclosures. Never opens cart or mutates variants. */
class SnoozeShopTheLook extends HTMLElement {
  connectedCallback() {
    this.frame = requestAnimationFrame(() => { if (this.isConnected) this.initialize(); });
  }
  initialize() {
    this.abort?.abort();
    this.abort = new AbortController();
    const { signal } = this.abort;
    this.markers = [...this.querySelectorAll('.snooze-look__marker')];
    this.cards = [...this.querySelectorAll('.snooze-look__card')];
    this.addEventListener('click', (event) => {
      const marker = event.target.closest('.snooze-look__marker');
      if (marker) {
        if (this.dataset.mode === 'always') {
          this.cardFor(marker)?.querySelector('a')?.focus({ preventScroll: true });
          this.cardFor(marker)?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        } else this.toggle(marker);
      }
      if (event.target.closest('.snooze-look__close')) this.close(true);
    }, { signal });
    document.addEventListener('click', (event) => {
      const onControl = event.composedPath().some((node) => this.cards.includes(node) || this.markers.includes(node));
      if (!onControl) this.close(false);
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.dataset.mode !== 'always') {
        this.close(true);
        event.stopPropagation();
      }
    }, { signal });
    document.addEventListener('shopify:block:select', (event) => {
      const marker = this.markers.find((m) => m.dataset.key === event.detail?.blockId);
      if (marker && this.dataset.mode !== 'always') this.toggle(marker, true, false);
    }, { signal });
    this.querySelectorAll('img').forEach((img) => img.addEventListener('load', () => this.place(), { signal }));
    this.observer = new ResizeObserver(() => this.place());
    this.observer.observe(this.querySelector('.snooze-look__stage'));
    window.addEventListener('resize', () => this.place(), { signal });
    document.fonts?.ready.then(() => { if (this.isConnected) this.place(); });
    this.place();
  }
  disconnectedCallback() { cancelAnimationFrame(this.frame); this.abort?.abort(); this.observer?.disconnect(); }
  cardFor(marker) { return this.cards.find((card) => card.dataset.key === marker.dataset.key); }
  close(restore) {
    if (this.dataset.mode === 'always') return;
    const active = this.markers.find((marker) => marker.getAttribute('aria-expanded') === 'true');
    this.cards.forEach((card) => { card.hidden = true; });
    this.markers.forEach((marker) => marker.setAttribute('aria-expanded', 'false'));
    if (restore) active?.focus({ preventScroll: true });
    this.place();
  }
  toggle(marker, force = false, focus = true) {
    const open = marker.getAttribute('aria-expanded') !== 'true' || force;
    this.close(false);
    if (!open) return;
    const card = this.cardFor(marker);
    if (!card) return;
    card.hidden = false;
    marker.setAttribute('aria-expanded', 'true');
    this.place();
    if (focus) {
      card.querySelector('.snooze-look__close')?.focus({ preventScroll: true });
      if (!this.hasAttribute('data-positioned')) card.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
  }
  place() {
    // Try bounded positions beside each marker. If any card cannot fit without
    // colliding with cards or markers, put ALL cards into the normal-flow grid.
    this.removeAttribute('data-positioned');
    this.cards.forEach((card) => { card.style.removeProperty('left'); card.style.removeProperty('top'); });
    const visible = this.cards.filter((card) => !card.hidden);
    if (window.innerWidth < 750 || !visible.length) return;
    const stage = this.querySelector('.snooze-look__stage').getBoundingClientRect();
    if (!stage.width) return;
    this.setAttribute('data-positioned', '');
    const markers = this.markers.map((m) => {
      const r = m.getBoundingClientRect();
      return { key: m.dataset.key, x: r.left - stage.left, y: r.top - stage.top, w: r.width, h: r.height };
    });
    const overlaps = (a, b) => a.x < b.x + b.w + 8 && a.x + a.w + 8 > b.x && a.y < b.y + b.h + 8 && a.y + a.h + 8 > b.y;
    const placed = [];
    for (const card of visible) {
      const marker = markers.find((m) => m.key === card.dataset.key);
      const { width: w, height: h } = card.getBoundingClientRect();
      const candidates = [
        { x: marker.x + marker.w + 12, y: marker.y },
        { x: marker.x - w - 12, y: marker.y },
        { x: marker.x, y: marker.y + marker.h + 12 },
        { x: marker.x, y: marker.y - h - 12 },
      ].map((point) => ({ x: Math.max(8, Math.min(point.x, stage.width - w - 8)), y: Math.max(8, Math.min(point.y, stage.height - h - 8)), w, h }));
      const position = candidates.find((p) => p.x + w <= stage.width - 8 && p.y + h <= stage.height - 8 && ![...placed, ...markers].some((other) => overlaps(p, other)));
      if (!position) {
        this.removeAttribute('data-positioned');
        this.cards.forEach((c) => { c.style.removeProperty('left'); c.style.removeProperty('top'); });
        return;
      }
      card.style.left = `${position.x}px`;
      card.style.top = `${position.y}px`;
      placed.push(position);
    }
  }
}
if (!customElements.get('snooze-shop-the-look')) customElements.define('snooze-shop-the-look', SnoozeShopTheLook);
